import { encodeParam } from "./encoder";
import invariant from "invariant";

function escapeRegExp(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function _compilePattern(pattern) {
  let regexpSource = "";
  const paramNames = [];
  const tokens = [];

  let match,
    lastIndex = 0,
    matcher = /:([a-zA-Z_$][a-zA-Z0-9_$]*)|\*\*|\*|\(|\)|\\\(|\\\)/g;

  // eslint-disable-next-line no-cond-assign
  while ((match = matcher.exec(pattern))) {
    if (match.index !== lastIndex) {
      tokens.push(pattern.slice(lastIndex, match.index));
      regexpSource += escapeRegExp(pattern.slice(lastIndex, match.index));
    }

    if (match[1]) {
      regexpSource += "([^/]+)";
      paramNames.push(match[1]);
    } else if (match[0] === "**") {
      regexpSource += "(.*)";
      paramNames.push("splat");
    } else if (match[0] === "*") {
      regexpSource += "(.*?)";
      paramNames.push("splat");
    } else if (match[0] === "(") {
      regexpSource += "(?:";
    } else if (match[0] === ")") {
      regexpSource += ")?";
    } else if (match[0] === "\\(") {
      regexpSource += "\\(";
    } else if (match[0] === "\\)") {
      regexpSource += "\\)";
    }

    tokens.push(match[0]);

    lastIndex = matcher.lastIndex;
  }

  if (lastIndex !== pattern.length) {
    tokens.push(pattern.slice(lastIndex, pattern.length));
    regexpSource += escapeRegExp(pattern.slice(lastIndex, pattern.length));
  }

  return {
    pattern,
    regexpSource,
    paramNames,
    tokens
  };
}

const CompiledPatternsCache = Object.create(null);

export function compilePattern(pattern) {
  if (!CompiledPatternsCache[pattern]) CompiledPatternsCache[pattern] = _compilePattern(pattern);

  return CompiledPatternsCache[pattern];
}

/**
 * Returns a version of the given pattern with params interpolated. Throws
 * if there is a dynamic segment of the pattern for which there is no param.
 */
export function formatPattern(pattern, params) {
  params = params || {};

  const { tokens } = compilePattern(pattern);
  let parenCount = 0,
    pathname = "",
    splatIndex = 0,
    parenHistory = [];

  let token, paramName, paramValue;
  for (let i = 0, len = tokens.length; i < len; ++i) {
    token = tokens[i];

    if (token === "*" || token === "**") {
      paramValue = Array.isArray(params.splat) ? params.splat[splatIndex++] : params.splat;

      invariant(paramValue != null || parenCount > 0, 'Missing splat #%s for path "%s"', splatIndex, pattern);

      if (paramValue != null) pathname += encodeParam(paramValue);
    } else if (token === "(") {
      parenHistory[parenCount] = "";
      parenCount += 1;
    } else if (token === ")") {
      const parenText = parenHistory.pop();
      parenCount -= 1;

      if (parenCount) parenHistory[parenCount - 1] += parenText;
      else pathname += parenText;
    } else if (token === "\\(") {
      pathname += "(";
    } else if (token === "\\)") {
      pathname += ")";
    } else if (token.charAt(0) === ":") {
      paramName = token.substring(1);
      paramValue = params[paramName];

      invariant(paramValue != null || parenCount > 0, 'Missing "%s" parameter for path "%s"', paramName, pattern);

      if (paramValue == null) {
        if (parenCount) {
          parenHistory[parenCount - 1] = "";

          const curTokenIdx = tokens.indexOf(token);
          const tokensSubset = tokens.slice(curTokenIdx, tokens.length);
          let nextParenIdx = -1;

          for (let i = 0; i < tokensSubset.length; i++) {
            if (tokensSubset[i] === ")") {
              nextParenIdx = i;
              break;
            }
          }

          invariant(nextParenIdx > 0, 'Path "%s" is missing end paren at segment "%s"', pattern, tokensSubset.join(""));

          // jump to ending paren
          i = curTokenIdx + nextParenIdx - 1;
        }
      } else if (parenCount) parenHistory[parenCount - 1] += encodeParam(paramValue);
      else pathname += encodeParam(paramValue);
    } else {
      if (parenCount) parenHistory[parenCount - 1] += token;
      else pathname += token;
    }
  }

  invariant(parenCount <= 0, 'Path "%s" is missing end paren', pattern);

  return pathname.replace(/\/+/g, "/");
}



// WEBPACK FOOTER //
// ./src/react-router-utils/patternUtils.js